import { toast } from 'sonner';
import { useMemo, useRef, useState } from 'react';
import { collection, doc } from '@firebase/firestore';

import { db } from '../../firebase-config.js';
import type { UploadStatus } from './types.ts';
import { UPLOAD_STATUS } from './constants.ts';
import { useFileUpload } from './useFileUpload.ts';
import { UploadStatusHandler, useFileUploadListener } from './useFileUploadListener.ts';

type OnStatusChange = NonNullable<Parameters<typeof useFileUploadListener>[1]['onStatusChange']>;

const getToast = (noToast: boolean | undefined) => {
  if (noToast) {
    return;
  }

  return toast;
};

const onStatusChange: CustomOnStatusChange = {
  UPLOADING: ({ id, noToast, cancel, reset }) => {
    getToast(noToast)?.loading('📤 Uploading file...', {
      id,
      action: {
        label: 'Cancel',
        onClick: () => {
          cancel();

          toast.warning('File upload cancelled');
        },
      },
      onAutoClose: () => {
        reset?.();
      },
      onDismiss: () => {
        reset?.();
      },
    });
  },
  UPLOADED: ({ id, noToast }) => {
    // We say that the file is being scanned, but it is actually between unscanned and scanned bucket.
    getToast(noToast)?.loading('🔍 Scanning file...', {
      id,
      description: undefined,
      action: undefined,
    });
  },
  SCANNING: ({ id, noToast }) => {
    getToast(noToast)?.loading('🔍 Scanning file...', {
      id,
      description: undefined,
      action: undefined,
    });
  },
  COMPLETED: ({ id, noToast }) => {
    getToast(noToast)?.success('File uploaded successfully', {
      id,
      description: undefined,
      action: undefined,
    });
  },
  CANCELLED: ({ id, noToast }) => {
    getToast(noToast)?.warning('File upload cancelled', {
      id,
      description: undefined,
      action: undefined,
    });
  },
  QUARANTINED: ({ id, noToast }) => {
    getToast(noToast)?.warning('File has been quarantined', {
      id,
      description: 'Please contact support if you believe this is a mistake.',
      action: undefined,
    });
  },
  FAILED: ({ id, noToast }) => {
    getToast(noToast)?.error('File failed to upload', {
      id,
      description: 'Please try again later. If the issue persists, please contact support.',
      action: undefined,
    });
  },
};

type BaseHandlerParam = Parameters<UploadStatusHandler>[0];

type ExtendedHandlerParam = BaseHandlerParam & {
  reset: () => void;
  cancel: () => void;
  noToast?: boolean;
};

type CustomOnStatusChange = Partial<Record<UploadStatus, (params: ExtendedHandlerParam) => void | Promise<void>>>;

interface UseFileUploaderOnStatusChangeOptions
  extends Omit<Parameters<typeof useFileUploadListener>[1], 'onStatusChange'> {
  onStatusChange?: CustomOnStatusChange;
}

type UseFileUploaderOptions = {
  listenerOptions?: UseFileUploaderOnStatusChangeOptions;
  noToast?: boolean;
};

/**
 * @description A hook that provides a _single_ file uploader and a listener for the upload status.
 * @param noToast - Whether to show a toast notification when the upload status changes.
 * @param listenerOptions - Options for the listener.
 */
export const useFileUploader = ({ noToast = false, listenerOptions }: UseFileUploaderOptions = {}) => {
  const [id, setId] = useState<string | undefined>(undefined);
  const { upload } = useFileUpload();

  const abortControllerRef = useRef<AbortController | null>(null);

  const onStatusChanges = useMemo(() => {
    const optionHandlers = listenerOptions?.onStatusChange || {};

    const reset = () => {
      setId(undefined);
      abortControllerRef.current = null;
    };

    const cancel = () => {
      abortControllerRef.current?.abort();
      reset();
    };

    return Object.keys(UPLOAD_STATUS).reduce<OnStatusChange>((acc, key) => {
      const baseHandler = onStatusChange[key as UploadStatus];
      const optionHandler = optionHandlers[key as UploadStatus];

      if (!baseHandler && !optionHandler) {
        return acc;
      }

      return {
        ...acc,
        [key]: async (params: BaseHandlerParam) => {
          await baseHandler?.({ ...params, reset, cancel, noToast });
          await optionHandler?.({ ...params, reset, cancel, noToast });
        },
      };
    }, {});
  }, [listenerOptions?.onStatusChange]);

  const data = useFileUploadListener(id, {
    ...listenerOptions,
    onStatusChange: onStatusChanges,
  });

  const uploader = async (params: Omit<Parameters<typeof upload>[0], 'ref'>) => {
    const fileRef = doc(collection(db, 'file_uploads'));
    setId(fileRef.id);

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const uploadFn = async () => await upload({ fileRef, signal: abortController.signal, ...params });

    const cancel = () => {
      abortController.abort();
      setId(undefined);
      abortControllerRef.current = null;
    };

    return {
      id: fileRef.id,
      upload: uploadFn,
      cancel,
    };
  };

  return {
    id,
    data,
    uploader,
  };
};
